import { Checklist, ChecklistScheduleDto, ScheduleFrequencyDays, ScheduleMonthlyFrequencyDays, ScheduleMonthlyFrequencyMonths, ScheduleMonthlyFrequencyOns, ScheduleType, ScheduleWeeklyFrequencyDay } from "../model/ChecklistDtos";
import { SchedulerData } from "../component/ChecklistScheduleCalendar";

type RecurrenceRule = string & { __brand: "RecurrenceRule" };

export const AppointmentMapper = {
    mapChecklistsToSchedulerData: function (items: Checklist[]): SchedulerData[] {
        return items
            .map(item => item.schedules
                .filter(e=> !!e.startDate)
                .flatMap(schedule => mapSchedule(schedule, item.name))
                .filter(Boolean) as SchedulerData[]
            ).flat()
    }
}

function mapSchedule(schedule: ChecklistScheduleDto, name: string): SchedulerData | undefined {
    switch (schedule.type) {
        case ScheduleType.Once:
            return OnceScheduleMapper.map(schedule, name)

        case ScheduleType.Daily:
            return DailyScheduleMapper.map(schedule, name)

        case ScheduleType.Weekly:
            return WeeklyScheduleMapper.map(schedule, name)

        case ScheduleType.FourWeekly:
            return FourWeeklyScheduleMapper.map(schedule, name)

        case ScheduleType.Monthly:
            return MonthlyScheduleMapper.map(schedule, name)
    }
}

const OnceScheduleMapper = {
    map: function (schedule: ChecklistScheduleDto, name: string): SchedulerData | undefined {
        if (!schedule.onceDate) return undefined
        return createSchedulerData(schedule, name, undefined)
    }
}

const DailyScheduleMapper = {
    map: function (schedule: ChecklistScheduleDto, name: string): SchedulerData | undefined {
        if (!schedule.startDate) return undefined
        return createSchedulerData(schedule, name, this.getDailyRecurrence(schedule))
    },
    getDailyRecurrence: function (schedule: ChecklistScheduleDto): RecurrenceRule {
        if (!schedule.startDate) return '' as RecurrenceRule

        let recurrenceRule = "FREQ=DAILY"
        const includedDays = findIncludedWeekDays(schedule.dailyFrequency ?? 0).toString()
        if (includedDays) {
            recurrenceRule += ";BYDAY=" + includedDays
        }
        if (schedule.endDate) {
            recurrenceRule += ";UNTIL=" + formatEndDate(new Date(schedule.endDate))
        }
        return recurrenceRule as RecurrenceRule
    }
}

const WeeklyScheduleMapper = {
    map: function (schedule: ChecklistScheduleDto, name: string): SchedulerData | undefined {
        return createSchedulerData(schedule, name, this.getWeeklyRecurrence(schedule))
    },
    getWeeklyRecurrence: function (schedule: ChecklistScheduleDto): RecurrenceRule {
        let recurrenceRule = "FREQ=WEEKLY";
        const includedWeekDay = findSingleWeekDay(schedule.weeklyFrequency)
        if (includedWeekDay) {
            recurrenceRule += ";BYDAY=" + includedWeekDay
        }
        if (schedule.endDate) {
            recurrenceRule += ";UNTIL=" + formatEndDate(new Date(schedule.endDate))
        }
        return recurrenceRule as RecurrenceRule
    }
}

const FourWeeklyScheduleMapper = {
    map: function (schedule: ChecklistScheduleDto, name: string): SchedulerData | undefined {
        return createSchedulerData(schedule, name, this.getFourWeeklyRecurrence(schedule))
    },
    getFourWeeklyRecurrence(schedule: ChecklistScheduleDto): RecurrenceRule {
        let recurrenceRule = "FREQ=WEEKLY;INTERVAL=4"
        const includedWeekDayFourWeekly = findSingleWeekDay(schedule.fourWeeklyFrequency)
        if (includedWeekDayFourWeekly) {
            recurrenceRule += ";BYDAY=" + includedWeekDayFourWeekly
        }
        if (schedule.endDate) {
            recurrenceRule += ";UNTIL=" + formatEndDate(new Date(schedule.endDate))
        }
        return recurrenceRule as RecurrenceRule
    }
}

const MonthlyScheduleMapper = {
    map: function (schedule: ChecklistScheduleDto, name: string): SchedulerData | undefined {
        if (schedule.monthlyFrequencyDay) {
            return createSchedulerData(schedule, name, this.getMonthlyRecurrenceForDaysType(schedule))
        }
        if (schedule.monthlyFrequencyOn && schedule.monthlyFrequencyOnDay) {
            return createSchedulerData(schedule, name, this.getMonthlyRecurrenceForOnType(schedule))
        }
    },
    getBaseMonthlyRecurrence: function (schedule: ChecklistScheduleDto): RecurrenceRule {
        let recurrenceRule = "FREQ=MONTHLY"
        const includedMonths = findIncludedMonths(schedule.monthlyFrequencyMonth ?? 0).toString()
        if (includedMonths) {
            recurrenceRule += ";BYMONTH=" + includedMonths
        }
        return recurrenceRule as RecurrenceRule

    },
    getMonthlyRecurrenceForDaysType: function (schedule: ChecklistScheduleDto): RecurrenceRule {
        const baseRecurrenceRule = this.getBaseMonthlyRecurrence(schedule)
        let recurrenceRule = ''
        const includedDates = findIncludedDates(schedule.monthlyFrequencyDay).toString()
        if (includedDates) {
            recurrenceRule += ";BYMONTHDAY=" + includedDates
        }
        if (schedule.endDate) {
            recurrenceRule += ";UNTIL=" + formatEndDate(new Date(schedule.endDate))
        }
        return `${baseRecurrenceRule}${recurrenceRule}` as RecurrenceRule
    },
    getMonthlyRecurrenceForOnType: function (schedule: ChecklistScheduleDto): RecurrenceRule {
        const baseRecurrenceRule = this.getBaseMonthlyRecurrence(schedule)
        const includedWeekDays = findIncludedWeekDays(schedule.monthlyFrequencyOnDay ?? 0)
        const includedWeekNumbers = getMonthlyPosition(schedule.monthlyFrequencyOn ?? 0)
        let recurrenceRule = generateByDayPart(includedWeekDays, includedWeekNumbers)
        if (schedule.endDate) {
            recurrenceRule += ";UNTIL=" + formatEndDate(new Date(schedule.endDate))
        }
        return `${baseRecurrenceRule}${recurrenceRule}` as RecurrenceRule
    }
}

function generateByDayPart(
  includedWeekDays: string[],
  includedWeekNumbers: string[]
): string | null {
  if (!includedWeekDays.length || !includedWeekNumbers.length) return null

  const byDayParts: string[] = []

  for (const day of includedWeekDays) {
    for (const pos of includedWeekNumbers) {
      byDayParts.push(`${pos}${day}`)
    }
  }

  return `;BYDAY=${byDayParts.join(',')}`
}


function findIncludedWeekDays(value?: number): string[] {
    if (!value) return []

    const includedDays: string[] = [];

    const dayAbbreviations: { [key: string]: string } = {
        Monday: "MO",
        Tuesday: "TU",
        Wednesday: "WE",
        Thursday: "TH",
        Friday: "FR",
        Saturday: "SA",
        Sunday: "SU",
    };

    for (const day in ScheduleFrequencyDays) {
        if (isNaN(Number(day))) {
            const dayValue = ScheduleFrequencyDays[day as keyof typeof ScheduleFrequencyDays];

            if ((value & dayValue) !== 0) {
                const abbreviation = dayAbbreviations[day];
                if (abbreviation) {
                    includedDays.push(abbreviation);
                }
            }
        }
    }
    return includedDays;
}

function findSingleWeekDay(value?: number): string {
    if (!value) return ''

    const dayAbbreviations: { [key: string]: string } = {
        Monday: "MO",
        Tuesday: "TU",
        Wednesday: "WE",
        Thursday: "TH",
        Friday: "FR",
        Saturday: "SA",
        Sunday: "SU",
    };

    const weekDay = ScheduleWeeklyFrequencyDay[value ?? -1]
    return dayAbbreviations[weekDay]
}

function findIncludedMonths(value: number): number[] {
    const includedMonths: number[] = [];

    for (let month in ScheduleMonthlyFrequencyMonths) {
        const monthValue: number =
            ScheduleMonthlyFrequencyMonths[month as keyof typeof ScheduleMonthlyFrequencyMonths];
        if ((value & monthValue) !== 0) {
            const monthIndex = Math.log2(monthValue) + 1;
            includedMonths.push(monthIndex);
        }
    }
    return includedMonths;
}

function getMonthlyPosition(position: number): string[] {
    const positionMap: { [key: number]: string } = {
        [ScheduleMonthlyFrequencyOns.First]: "1",
        [ScheduleMonthlyFrequencyOns.Second]: "2",
        [ScheduleMonthlyFrequencyOns.Third]: "3",
        [ScheduleMonthlyFrequencyOns.Fourth]: "4",
        [ScheduleMonthlyFrequencyOns.Last]: "-1",
    };

    const includedPositions: string[] = [];

    for (const key in positionMap) {
        if (position & Number(key)) {
            includedPositions.push(positionMap[Number(key)]);
        }
    }

    return includedPositions
}

function findIncludedDates(value?: number): number[] {
    if (!value) return []

    const includedDays: number[] = [];

    for (let day in ScheduleMonthlyFrequencyDays) {
        const dayValue: number = ScheduleMonthlyFrequencyDays[day as keyof typeof ScheduleMonthlyFrequencyDays];

        if (day !== "Last" && (value & dayValue) !== 0) {
            const dayIndex = Math.log2(dayValue) + 1;
            includedDays.push(dayIndex);
        }
    }

    if ((value & ScheduleMonthlyFrequencyDays.Last) !== 0) {
        includedDays.push(-1);
    }

    return includedDays.sort((a, b) => a - b)
}

function formatEndDate(dateInput: Date): string | null {
    if (!dateInput) return null;

    const date = new Date(dateInput)
    date.setDate(date.getDate() + 1)

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const formattedMonth = month < 10 ? `0${month}` : month.toString();
    const formattedDay = day < 10 ? `0${day}` : day.toString();

    return `${year}${formattedMonth}${formattedDay}`
}

function createSchedulerData(
    schedule: ChecklistScheduleDto,
    text: string,
    recurrenceRule?: RecurrenceRule,
): SchedulerData | undefined {
    if (!schedule.startDate) return undefined
    return {
        id: schedule.idChecklistSchedule,
        idChecklist: schedule.idChecklist,
        text: text,
        startDate: new Date(schedule.startDate),
        endDate: new Date(new Date(schedule.startDate).setHours(13, 59, 59)),
        description: schedule.idChecklist?.toString() ?? "",
        recurrenceRule: recurrenceRule,
        allDay: !schedule.startDate || !schedule.endDate,
    };
}
